import { get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import HotelBenchmarkInsightTooltipsSharedTooltipTableRow from 'lumen/components/hotel/benchmark-insight/tooltips/shared/tooltip-table-row';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS,
  type Kpi,
  KPI_LONG_TRANSLATIONS,
  KPI_SHORT_TRANSLATIONS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

export interface ComparisonSetVisibilityFlags {
  showComparisonSet: boolean;
  showPickupColumn: boolean;
  showReferencePeriodColumn: boolean;
}

interface HotelBenchmarkInsightTooltipsSharedTooltipTableSignature {
  Args: {
    isPast: boolean;
    ownComparisonSetBreakdown: BeniBreakdown | null;
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    primaryComparisonSetBreakdown: BeniBreakdown | null;
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetBreakdown: BeniBreakdown | null;
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedKpi: Kpi;
    showMix: boolean;
    showPickup: boolean;
    varianceTranslation?: string;
    visibleKpis: Kpi[];
  };
}

export default class HotelBenchmarkInsightTooltipsSharedTooltipTable extends Component<HotelBenchmarkInsightTooltipsSharedTooltipTableSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  get correctPickupLabel(): string {
    const { showMix } = this.args;

    const labelDefault = this.intl.t('benchmark_insight.pickup');
    const labelMix = this.intl.t(
      'benchmark_insight.los_chart.tooltip.mix_view.pickup_label',
    );
    const isMix = showMix;

    return isMix ? labelMix : labelDefault;
  }

  get ownComparisonSetVisibilityFlags(): ComparisonSetVisibilityFlags {
    const { ownComparisonSetMeta } = this.args;

    return this.getComparisonSetVisibilityFlags(ownComparisonSetMeta);
  }

  get primaryComparisonSetTranslation(): string {
    const { primaryComparisonSetMeta } = this.args;

    // We already check in the template if the comparison set is present, so we can safely cast the meta to BeniMetaComparisonSet.
    return this.getTranslationForComparisonSet(
      primaryComparisonSetMeta as BeniMetaComparisonSet,
    );
  }

  get primaryComparisonSetVisibilityFlags(): ComparisonSetVisibilityFlags {
    const { primaryComparisonSetMeta } = this.args;

    return this.getComparisonSetVisibilityFlags(primaryComparisonSetMeta);
  }

  get secondaryComparisonSetTranslation(): string {
    const { secondaryComparisonSetMeta } = this.args;

    return this.getTranslationForComparisonSet(
      secondaryComparisonSetMeta as BeniMetaComparisonSet,
    );
  }

  get secondaryComparisonSetVisibilityFlags(): ComparisonSetVisibilityFlags {
    const { secondaryComparisonSetMeta } = this.args;

    return this.getComparisonSetVisibilityFlags(secondaryComparisonSetMeta);
  }

  get selectedKpiTranslation(): string {
    const { selectedKpi } = this.args;

    return (
      KPI_SHORT_TRANSLATIONS[selectedKpi] ?? KPI_LONG_TRANSLATIONS[selectedKpi]
    );
  }

  getComparisonSetVisibilityFlags(
    comparisonSetMeta: BeniMetaComparisonSet | null,
  ): ComparisonSetVisibilityFlags {
    if (!comparisonSetMeta) {
      return {
        showComparisonSet: false,
        showPickupColumn: false,
        showReferencePeriodColumn: false,
      };
    }

    return {
      showComparisonSet:
        this.showCurrentPeriodColumnForComparisonSet(comparisonSetMeta),
      showPickupColumn:
        this.showPickupColumnForComparisonSet(comparisonSetMeta),
      showReferencePeriodColumn:
        this.showReferencePeriodColumnForComparisonSet(comparisonSetMeta),
    };
  }

  getTranslationForComparisonSet(
    comparisonSetMeta: BeniMetaComparisonSet,
  ): string {
    return BENI_COMPARISON_SET_LONG_TRANSLATIONS[
      comparisonSetMeta.comparisonSetType
    ];
  }

  showCurrentPeriodColumnForComparisonSet(
    comparisonSetMeta: BeniMetaComparisonSet | null,
  ): boolean {
    return Boolean(comparisonSetMeta);
  }

  showPickupColumnForComparisonSet(
    comparisonSetMeta: BeniMetaComparisonSet | null,
  ): boolean {
    if (!comparisonSetMeta) {
      return false;
    }

    return Boolean(
      this.access.hasBenchmarkInsightPickupData &&
      this.args.showPickup &&
      !this.args.isPast,
    );
  }

  showReferencePeriodColumnForComparisonSet(
    comparisonSetMeta: BeniMetaComparisonSet | null,
  ): boolean {
    if (!comparisonSetMeta) {
      return false;
    }

    return Boolean(
      this.access.hasBenchmarkInsightReferencePeriodData &&
      comparisonSetMeta.hasReferencePeriodData,
    );
  }

  <template>
    <table class="tooltip-table default-table">
      <thead class="tooltip-table__header">
        <th class="tooltip-table__header__cell">
          <div class="flex-start flex flex-row items-center">
            {{t "benchmark_insight.kpis"}}
          </div>
        </th>

        {{#if this.ownComparisonSetVisibilityFlags.showComparisonSet}}
          <th class="tooltip-table__header__cell section-border">
            <div class="flex flex-row justify-end">
              {{t "general.my_hotel"}}
            </div>
          </th>
          {{#if this.ownComparisonSetVisibilityFlags.showPickupColumn}}
            <th class="tooltip-table__header__cell">
              <div
                class="flex flex-row justify-end"
              >{{this.correctPickupLabel}}</div>
            </th>
          {{/if}}
          {{#if this.ownComparisonSetVisibilityFlags.showReferencePeriodColumn}}
            <th class="tooltip-table__header__cell">
              <div class="flex flex-row justify-end">
                {{t
                  (get
                    BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS @referencePeriod
                  )
                }}
              </div>
            </th>
          {{/if}}
        {{/if}}

        {{#if this.primaryComparisonSetVisibilityFlags.showComparisonSet}}
          <th class="tooltip-table__header__cell section-border">
            <div class="flex flex-row justify-end">
              {{t this.primaryComparisonSetTranslation}}
            </div>
          </th>
          {{#if this.primaryComparisonSetVisibilityFlags.showPickupColumn}}
            <th class="tooltip-table__header__cell">
              <div class="flex flex-row justify-end">
                {{this.correctPickupLabel}}
              </div>
            </th>
          {{/if}}
          {{#if
            this.primaryComparisonSetVisibilityFlags.showReferencePeriodColumn
          }}
            <th class="tooltip-table__header__cell">
              <div class="flex flex-row justify-end">
                {{t
                  (get
                    BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS @referencePeriod
                  )
                }}
              </div>
            </th>
          {{/if}}
        {{/if}}

        {{#if this.secondaryComparisonSetVisibilityFlags.showComparisonSet}}
          <th class="tooltip-table__header__cell section-border">
            <div class="flex flex-row justify-end">
              {{t this.secondaryComparisonSetTranslation}}
            </div>
          </th>

          {{#if
            this.secondaryComparisonSetVisibilityFlags.showReferencePeriodColumn
          }}
            <th class="tooltip-table__header__cell">
              <div class="flex flex-row justify-end">
                {{t
                  (get
                    BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS @referencePeriod
                  )
                }}
              </div>
            </th>
          {{/if}}
        {{/if}}
      </thead>

      <tbody>
        {{#each @visibleKpis as |visibleKpi|}}
          <HotelBenchmarkInsightTooltipsSharedTooltipTableRow
            @kpi={{visibleKpi}}
            @ownComparisonSetBreakdown={{@ownComparisonSetBreakdown}}
            @ownComparisonSetVisibilityFlags={{this.ownComparisonSetVisibilityFlags}}
            @primaryComparisonSetBreakdown={{@primaryComparisonSetBreakdown}}
            @primaryComparisonSetVisibilityFlags={{this.primaryComparisonSetVisibilityFlags}}
            @secondaryComparisonSetBreakdown={{@secondaryComparisonSetBreakdown}}
            @secondaryComparisonSetVisibilityFlags={{this.secondaryComparisonSetVisibilityFlags}}
            @selectedKpi={{@selectedKpi}}
            @showMix={{@showMix}}
            @varianceTranslation={{@varianceTranslation}}
          />
        {{/each}}
      </tbody>
    </table>
  </template>
}
